import React, { useEffect, useContext, useRef } from 'react';
import loadable from '@loadable/component';
import { useLocation, useHistory, Route, Switch } from 'react-router-dom';
import lazysizes from 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import 'lazysizes/plugins/optimumx/ls.optimumx';
import classNames from 'classnames';
import ErrorHandler from './components/ErrorHandler';
import { breakpoints } from './config/constants';
import Navigation from './components/Navigation';
import { reloadable } from './components/Reloadable';
import DecodeGlobalContext from './redux/decodeGlobalContext';
import { isViewingArticle } from './helpers/utils';
import './assets/css/global.scss';
import 'ssr-intersection-observer';

// https://github.com/aFarkas/lazysizes#js-api
// custom config of lazysizes for lazyloading
// expand - viewport expansion
// expFactor - factor to download images if in idle mode
lazysizes.cfg.loadMode = 1;
lazysizes.cfg.expand = 1000;
lazysizes.cfg.expFactor = 3;

const fallback = () => null;
const Home = loadable(() => import(/* webpackChunkName: "DecodeHome" */ './pages/Home'), {
  fallback
});

const Article = loadable(() => import(/* webpackChunkName: "DecodeArticle" */ './pages/Article'), {
  fallback
});

const App = () => {
  const location = useLocation();
  const history = useHistory();
  const homeScrollRef = useRef(0);
  const { state, dispatch } = useContext(DecodeGlobalContext);
  const { isTransported, homepageHeaderOffset } = state;

  function onBreakpointMatch(breakpoint) {
    let offset = 0;

    switch (breakpoint) {
      case 'mobile':
        offset = 80;
        break;
      case 'tablet':
        offset = 160;
        break;
      default:
    }

    dispatch({
      type: 'SET_HOMEPAGE_HEADER_OFFSET',
      payload: offset
    });
  }

  useEffect(() => {
    const searchText = (location && location.search) || '';
    // Save category text if any
    const queryString = searchText.length ? searchText.substr(1).split('=') : [];

    if (isViewingArticle(location)) {
      homeScrollRef.current = window.pageYOffset + homepageHeaderOffset; // Nav offset
      dispatch({ type: 'SET_ARTICLE_CARDS_LOADED', payload: false });
    } else if (isTransported) {
      state.categoryFilter.length && history.replace(`/decode?category=${state.categoryFilter}`);
    } else {
      // Not viewing an article and not transported.
      // TODO: We reset the filter?
      dispatch({
        type: 'SET_CATEGORY_FILTER',
        payload: queryString.length > 1 && queryString[0] === 'category' ? queryString[1] : ''
      });
      // TODO: We're... transported?
      dispatch({ type: 'SET_IS_TRANSPORTED', payload: true });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const isMobileView = window.innerWidth < breakpoints.smallDesktop;
    const willFixSideScroll = isMobileView && isViewingArticle(location);

    document.body.classList.toggle('side-scroll-fix', willFixSideScroll);

    if (isTransported) {
      window.scrollTo(0, homeScrollRef.current - homepageHeaderOffset);

      dispatch({ type: 'SET_IS_TRANSPORTED', payload: false });
    } else {
      window.scrollTo(0, 1);
    }
  }, [location, isViewingArticle]);

  useEffect(() => {
    // Update home header offset on breakpoint change
    const mobileMedia = window.matchMedia('(max-width: 767px)');
    const tabletMedia = window.matchMedia('(min-width: 768px) and (max-width: 1247px)');
    const desktopMedia = window.matchMedia('(min-width: 1248px)');

    mobileMedia.addListener(({ matches }) => matches && onBreakpointMatch('mobile'));
    tabletMedia.addListener(({ matches }) => matches && onBreakpointMatch('tablet'));
    desktopMedia.addListener(({ matches }) => matches && onBreakpointMatch('desktop'));

    // Initial breakpoint call
    onBreakpointMatch(mobileMedia.matches ? 'mobile' : tabletMedia.matches ? 'tablet' : 'desktop');
  }, []);

  return (
    <ErrorHandler>
      <div className={classNames('decode', { 'decode--article': isViewingArticle(location) })}>
        <Navigation />
        <main id="main-content">
          <Switch>
            <Route exact path="/decode/article/:articleName" render={() => <Article />} />
            <Route
              path="/decode"
              render={() => (
                <Home scrollY={homeScrollRef.current} categoryText={state.categoryFilter} />
              )}
            />
            <Route
              path="*"
              render={() => {
                throw new Error('No Entry');
              }}
            />
          </Switch>
        </main>
      </div>
    </ErrorHandler>
  );
};

export default reloadable(App);
